import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Briefcase,
  GraduationCap,
  Plant,
  Cpu,
  Users,
  ArrowRight,
  CheckCircle,
  EnvelopeSimple,
  Phone,
  MapPin,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const jobCategories = [
  {
    category: "TVET & Education",
    icon: GraduationCap,
    color: "green",
    roles: [
      { title: "TVET Program Director", type: "Full-time", location: "Bombo, Luweero" },
      { title: "Vocational Instructors", type: "Full-time", location: "Bombo, Luweero" },
      { title: "School Administrator", type: "Full-time", location: "Bombo, Luweero" },
      { title: "Admissions Officer", type: "Full-time", location: "Bombo, Luweero" },
    ],
  },
  {
    category: "Agriculture & Farming",
    icon: Plant,
    color: "amber",
    roles: [
      { title: "Farm Manager", type: "Full-time", location: "Bombo, Luweero" },
      { title: "Agricultural Officer", type: "Full-time", location: "Central Uganda" },
      { title: "Fish Farm Manager", type: "Full-time", location: "Bombo, Luweero" },
      { title: "Farm Workers", type: "Full-time / Part-time", location: "Bombo, Luweero" },
    ],
  },
  {
    category: "ICT & Technology",
    icon: Cpu,
    color: "cyan",
    roles: [
      { title: "ICT Volunteers & Interns", type: "Volunteer / Intern", location: "Bombo, Luweero" },
    ],
  },
];

export default function CareersPage() {
  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="careers-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-green-950 via-green-900 to-green-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(217,119,6,0.1)_0%,_transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-4 block">
              Careers & Opportunities
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-6">
              Join Our Growing Team of{" "}
              <span className="text-amber-400">Changemakers</span>
            </h1>
            <p className="text-lg text-green-100/80 leading-relaxed mb-8">
              PEDA AFRICA is recruiting qualified staff, trainers, interns, and volunteers across TVET education, agriculture, ICT, entrepreneurship, and community development.
            </p>
            <Button
              asChild
              size="lg"
              className="bg-amber-500 hover:bg-amber-600 text-green-950 font-semibold rounded-xl px-8"
              data-testid="careers-apply-button"
            >
              <a href="#open-positions">
                View Open Positions <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
              </a>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-16 md:py-24 bg-white" data-testid="why-join-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
                Why Join PEDA AFRICA
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-6">
                Work, volunteer, or train with us.
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                We welcome applications from passionate professionals, graduates, and community members who want to contribute to sustainable growth and transformation across Uganda.
              </p>
              <div className="space-y-4">
                {[
                  { title: "Meaningful Impact", desc: "Work directly on programs that empower youth, women, and communities" },
                  { title: "Professional Growth", desc: "Access training, mentorship, and career development opportunities" },
                  { title: "Community-Centered", desc: "Be part of a mission-driven team making real change in Bombo and beyond" },
                  { title: "Flexible Opportunities", desc: "Full-time roles, internships, and volunteer positions available" },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-700 shrink-0 mt-0.5" weight="fill" />
                    <div>
                      <p className="font-semibold text-slate-900">{item.title}</p>
                      <p className="text-sm text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/0tfpxi02_PP.png"
                alt="PEDA Training"
                className="w-full h-52 object-cover rounded-2xl shadow-lg col-span-2"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/0s4xigoz_ICT.png"
                alt="ICT Lab"
                className="w-full h-44 object-cover rounded-2xl shadow-lg"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/ymbseg05_AGRIC.png"
                alt="Agriculture"
                className="w-full h-44 object-cover rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section id="open-positions" className="py-16 md:py-24 bg-slate-50" data-testid="open-positions-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
              Open Positions
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              Current Opportunities
            </h2>
            <p className="text-base text-slate-600">
              Explore our current openings across education, agriculture, and technology.
            </p>
          </div>

          <div className="space-y-10">
            {jobCategories.map((cat, catIdx) => {
              const Icon = cat.icon;
              return (
                <motion.div
                  key={cat.category}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: catIdx * 0.1 }}
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      cat.color === "green" ? "bg-green-100" : cat.color === "amber" ? "bg-amber-100" : "bg-cyan-100"
                    }`}>
                      <Icon className={`h-5 w-5 ${
                        cat.color === "green" ? "text-green-700" : cat.color === "amber" ? "text-amber-700" : "text-cyan-700"
                      }`} weight="duotone" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900">{cat.category}</h3>
                    <span className="px-2.5 py-1 bg-slate-200 text-slate-600 text-xs font-medium rounded-full">
                      {cat.roles.length} {cat.roles.length === 1 ? "role" : "roles"}
                    </span>
                  </div>

                  <div className="grid gap-3">
                    {cat.roles.map((role) => (
                      <div
                        key={role.title}
                        className="bg-white rounded-2xl border border-slate-100 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:border-green-200 hover:shadow-sm transition-all"
                        data-testid={`job-card-${role.title.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <div>
                          <h4 className="font-semibold text-slate-900">{role.title}</h4>
                          <div className="flex flex-wrap items-center gap-3 mt-1.5 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Briefcase className="h-3.5 w-3.5" weight="duotone" />
                              {role.type}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3.5 w-3.5" weight="duotone" />
                              {role.location}
                            </span>
                          </div>
                        </div>
                        <Button
                          asChild
                          variant="outline"
                          className="border-green-700 text-green-700 hover:bg-green-700 hover:text-white rounded-xl px-5 text-sm transition-colors shrink-0"
                        >
                          <a href="mailto:hr@pedafrica.org?subject=Application: {role.title}">
                            Apply Now
                          </a>
                        </Button>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Apply CTA */}
      <section className="py-16 md:py-24 bg-green-950" data-testid="careers-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6">
                Ready to Make a Difference?
              </h2>
              <p className="text-base text-green-100/80 leading-relaxed mb-8">
                Send your CV, cover letter, and relevant documents to our HR department. We review applications on a rolling basis.
              </p>
              <div className="space-y-4 text-base">
                <div className="flex items-center gap-3 text-green-100">
                  <EnvelopeSimple className="h-5 w-5 text-amber-400 shrink-0" weight="duotone" />
                  <span>hr@pedafrica.org</span>
                </div>
                <div className="flex items-center gap-3 text-green-100">
                  <Phone className="h-5 w-5 text-amber-400 shrink-0" weight="duotone" />
                  <span>+256 761 834 803</span>
                </div>
                <div className="flex items-center gap-3 text-green-100">
                  <MapPin className="h-5 w-5 text-amber-400 shrink-0" weight="duotone" />
                  <span>Bombo, Luweero District, Uganda</span>
                </div>
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur rounded-2xl border border-white/10 p-8">
              <Users className="h-10 w-10 text-amber-400 mb-4" weight="duotone" />
              <h3 className="text-2xl font-bold text-white mb-4">Submit Your Interest</h3>
              <p className="text-green-100/70 text-sm leading-relaxed mb-6">
                Whether you're a seasoned professional, a recent graduate, or a community volunteer, there's a place for you at PEDA AFRICA.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  asChild
                  className="bg-amber-500 hover:bg-amber-600 text-green-950 font-semibold rounded-xl px-6"
                  data-testid="careers-email-button"
                >
                  <a href="mailto:hr@pedafrica.org">
                    Email Your CV <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="bg-white/10 hover:bg-white/20 border-white/25 text-white rounded-xl px-6"
                  data-testid="careers-contact-button"
                >
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
